<script>
import Thumbnail from 'dashboard/components/widgets/Thumbnail.vue';
import { dynamicTime } from 'shared/helpers/timeHelper';
import { mapGetters } from 'vuex';
import { utcToZonedTime } from 'date-fns-tz';
import { DEFAULT_ACCOUNT_TIMEZONE } from 'dashboard/constants/account';

export default {
  components: {
    Thumbnail,
  },
  props: {
    notificationItem: {
      type: Object,
      default: () => {},
    },
  },
  computed: {
    ...mapGetters({
      currentAccount: 'getCurrentAccount',
    }),
    notificationAssignee() {
      const {
        primary_actor_type: primaryActorType,
        primary_actor: primaryActor,
        secondary_actor: secondaryActor,
        notification_type: notificationType,
        user,
      } = this.notificationItem;

      if (primaryActorType === 'Conversation') {
        return primaryActor?.meta?.assignee;
      }

      if (primaryActorType === 'Task') {
        if (notificationType === 'task_completed') {
          return secondaryActor;
        }
        return user;
      }

      return primaryActor?.meta?.assignee;
    },
    hasNotificationAssignee() {
      return !!this.notificationAssignee;
    },
    notificationAssigneeName() {
      return this.notificationAssignee?.name || '';
    },
    notificationAssigneeThumbnail() {
      return this.notificationAssignee?.thumbnail || '';
    },

    // Novo computed para reformatar mensagem com timezone da conta
    formattedPushMessageTitle() {
      const originalMessage = this.notificationItem.push_message_title;

      // Verificar se é notificação de tarefa e tem due_date no meta
      if (this.isTaskNotification && this.notificationItem.meta?.due_date) {
        return this.formatTaskNotificationMessage(originalMessage);
      }

      return originalMessage;
    },

    isTaskNotification() {
      return ['task_due_tomorrow', 'task_due_today', 'task_overdue'].includes(
        this.notificationItem.notification_type
      );
    },

    // Detectar locale do sistema automaticamente
    systemLocale() {
      return navigator.language || navigator.languages?.[0] || 'en-US';
    },

    accountTimezone() {
      return this.currentAccount?.timezone || DEFAULT_ACCOUNT_TIMEZONE;
    },
  },
  methods: {
    dynamicTime,

    formatTaskNotificationMessage(originalMessage) {
      try {
        const dueDateISO = this.notificationItem.meta.due_date;
        const dueDate = new Date(dueDateISO);

        if (Number.isNaN(dueDate.getTime())) {
          return originalMessage;
        }

        // Formatar data no timezone e locale local do sistema
        const localFormattedDate = this.formatDateToLocalTimezone(dueDate);

        // Validar se a formatação gerou uma string válida
        if (!localFormattedDate || localFormattedDate.trim() === '') {
          return originalMessage;
        }

        // Primeiro tentar substituir qualquer data existente na mensagem
        const formattedMessage = this.replaceDateInMessage(
          originalMessage,
          localFormattedDate
        );

        // Se a substituição funcionou, retornar o resultado
        if (formattedMessage !== originalMessage) {
          return formattedMessage;
        }

        // Se não encontrou padrão para substituir, ADICIONAR a data na mensagem
        // Especialmente importante para task_overdue que não inclui data por padrão
        return this.addDateToMessage(originalMessage, localFormattedDate);
      } catch (error) {
        return originalMessage;
      }
    },

    addDateToMessage(originalMessage, formattedDate) {
      // Para diferentes tipos de notificação, adicionar a data em locais apropriados
      const notificationType = this.notificationItem.notification_type;

      if (notificationType === 'task_overdue') {
        // "Tarefa 'Compra' venceu relacionada a Jairo"
        // → "Tarefa 'Compra' venceu em 25 jun, 14:30 relacionada a Jairo"
        return originalMessage.replace(
          ' relacionada',
          ` em ${formattedDate} relacionada`
        );
      }

      if (notificationType === 'task_due_tomorrow') {
        // Se não tem data, adicionar após o nome da tarefa
        // "Tarefa 'Teste' vence amanhã relacionada a João"
        // → "Tarefa 'Teste' vence amanhã (25 jun, 14:30) relacionada a João"
        return originalMessage.replace(
          ' relacionada',
          ` (${formattedDate}) relacionada`
        );
      }

      if (notificationType === 'task_due_today') {
        // "Tarefa 'Teste' vence hoje relacionada a João"
        // → "Tarefa 'Teste' vence hoje (25 jun, 14:30) relacionada a João"
        return originalMessage.replace(
          ' relacionada',
          ` (${formattedDate}) relacionada`
        );
      }

      // Para outros tipos, simplesmente adicionar a data no final
      return `${originalMessage} (${formattedDate})`;
    },

    formatDateToLocalTimezone(dueDate) {
      try {
        const accountDate = utcToZonedTime(dueDate, this.accountTimezone);
        const hasTime =
          accountDate.getHours() !== 0 || accountDate.getMinutes() !== 0;

        // Opções de formatação baseadas no locale do sistema
        const options = {
          day: 'numeric',
          month: 'short',
          timeZone: this.accountTimezone,
        };

        // Incluir ano se for diferente do ano atual
        const currentYear = utcToZonedTime(
          new Date(),
          this.accountTimezone
        ).getFullYear();
        if (accountDate.getFullYear() !== currentYear) {
          options.year = 'numeric';
        }

        // Incluir horário se não for meia-noite
        if (hasTime) {
          options.hour = '2-digit';
          options.minute = '2-digit';
        }

        const formatted = dueDate.toLocaleDateString(
          this.systemLocale,
          options
        );

        // Validar resultado da formatação
        if (!formatted || formatted === 'Invalid Date') {
          // Fallback para formato simples
          return dueDate.toLocaleString(this.systemLocale, {
            day: 'numeric',
            month: 'short',
            hour: hasTime ? '2-digit' : undefined,
            minute: hasTime ? '2-digit' : undefined,
            timeZone: this.accountTimezone,
          });
        }

        return formatted;
      } catch (error) {
        // Fallback simples
        return dueDate.toLocaleDateString();
      }
    },

    replaceDateInMessage(originalMessage, newFormattedDate) {
      if (!originalMessage || !newFormattedDate) {
        return originalMessage;
      }

      // Padrões específicos baseados no formato real do Rails I18n.l(format: :short)
      const patterns = [
        // Rails gera formatos como "Jun 25" ou "Jun 25 20:32"
        {
          regex: /\(([A-Za-z]{3} \d{1,2}(?:\s+\d{1,2}:\d{2})?)\)/,
        },
        // Padrões específicos em português para task_due_tomorrow/today
        {
          regex: /vence amanhã \(([^)]+)\)/,
        },
        {
          regex: /vence hoje \(([^)]+)\)/,
        },
        {
          regex: /venceu em ([A-Za-z]{3} \d{1,2}(?:\s+\d{1,2}:\d{2})?)\./,
        },
        // Padrões em inglês
        {
          regex: /due tomorrow \(([^)]+)\)/i,
        },
        {
          regex: /due today \(([^)]+)\)/i,
        },
        {
          regex: /due on ([A-Za-z]{3} \d{1,2}(?:\s+\d{1,2}:\d{2})?)\./i,
        },
        {
          regex: /overdue since ([A-Za-z]{3} \d{1,2}(?:\s+\d{1,2}:\d{2})?)\./i,
        },
      ];

      for (const pattern of patterns) {
        const match = originalMessage.match(pattern.regex);
        if (match && match[1]) {
          return originalMessage.replace(match[1], newFormattedDate);
        }
      }

      // Se nenhum padrão específico foi encontrado, tentar padrões mais genéricos
      const genericPatterns = [
        // Formato Rails comum: "Jun 25" ou "Jun 25 20:32"
        /\b([A-Za-z]{3} \d{1,2}(?:\s+\d{1,2}:\d{2})?)\b/g,
        // Formato brasileiro: 15/01/2024
        /\b(\d{1,2}\/\d{1,2}\/\d{4}[^.)]*)/g,
        // Formato abreviado: 15 Jan
        /\b(\d{1,2} \w{3}[^.)]*)/g,
        // Data dentro de parênteses (genérico)
        /\(([^)]+)\)/,
      ];

      for (const pattern of genericPatterns) {
        const match = originalMessage.match(pattern);
        if (match && match[1]) {
          return originalMessage.replace(match[1], newFormattedDate);
        }
      }

      // Se ainda não encontrou, fazer um fallback mais agressivo
      // Procurar qualquer sequência que pareça data dentro de parênteses
      const parenthesesMatch = originalMessage.match(/\(([^)]*)\)/);
      if (
        parenthesesMatch &&
        parenthesesMatch[1] &&
        parenthesesMatch[1].trim()
      ) {
        return originalMessage.replace(parenthesesMatch[1], newFormattedDate);
      }

      // Se não encontrou padrão específico, retornar mensagem original
      return originalMessage;
    },

    onClickOpenNotification() {
      this.$emit('openNotification', this.notificationItem);
    },
  },
};
</script>

<template>
  <div class="w-full">
    <woot-button
      size="expanded"
      color-scheme="secondary"
      variant="link"
      class="w-full"
      @click="onClickOpenNotification()"
    >
      <div
        class="flex-row items-center p-2.5 leading-[1.4] border-b border-solid border-line-faint flex w-full hover:bg-surface-sunken hover:rounded-lg"
      >
        <div
          v-if="!notificationItem.read_at"
          class="w-2 h-2 rounded-full bg-woot-500"
        />
        <div v-else class="flex w-2" />
        <div
          class="flex-col ml-2.5 overflow-hidden w-full flex justify-between"
        >
          <div class="flex justify-between">
            <div class="flex items-center">
              <span class="font-bold text-body">
                {{
                  `#${
                    notificationItem.primary_actor
                      ? notificationItem.primary_actor.id
                      : notificationItem.primary_actor_id ||
                        $t(`NOTIFICATIONS_PAGE.DELETE_TITLE`)
                  }`
                }}
              </span>
              <span
                class="text-xxs p-0.5 px-1 my-0 mx-2 bg-surface-hover rounded-md"
              >
                {{
                  $t(
                    `NOTIFICATIONS_PAGE.TYPE_LABEL.${notificationItem.notification_type}`
                  )
                }}
              </span>
            </div>
            <div v-if="hasNotificationAssignee">
              <Thumbnail
                :src="notificationAssigneeThumbnail"
                size="16px"
                :username="notificationAssigneeName"
              />
            </div>
          </div>
          <div class="flex w-full">
            <span
              class="overflow-hidden font-normal text-muted whitespace-nowrap text-ellipsis"
            >
              <!-- Usar a mensagem formatada com timezone da conta automaticamente -->
              {{ formattedPushMessageTitle }}
            </span>
          </div>
          <span class="flex mt-1 font-semibold text-subtle text-xxs">
            {{ dynamicTime(notificationItem.last_activity_at) }}
          </span>
        </div>
      </div>
    </woot-button>
  </div>
</template>
